import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdChevronRight, MdDelete, MdNoteAdd } from 'react-icons/md';
import { usePanelToast } from '../../../context/PanelToastContext';
import {
  localDateKey,
  suggestNextCoaChildCode,
  mergeCoaTreeWithLiveData,
  isCoaCashBankExpenseParent,
} from './financeData';
import {
  fetchCoaTree,
  createCoaChildRemote,
  createCoaLedgerEntryRemote,
  deleteCoaChildRemote,
} from '../../../services/admin/financeLedgerApi';

function emptyChildForm(parentCode, usedCodes = []) {
  return {
    parentCode: parentCode || '',
    name: '',
    code: parentCode ? suggestNextCoaChildCode(parentCode, usedCodes) : '',
    description: '',
  };
}

function emptyEntryForm(childCode = '') {
  return {
    childCode,
    date: localDateKey(),
    description: '',
    debit: '',
    credit: '',
    ref: '',
    paymentMethod: 'cash',
  };
}

/**
 * CoA parent cards styled like folder account boxes.
 * Add-child icon in header only; add-entry icon beside each child.
 */
function CoaAccountTree({ gymId, liveData }) {
  const navigate = useNavigate();
  const toast = usePanelToast();
  const [baseTree, setBaseTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChildModal, setShowChildModal] = useState(false);
  const [childForm, setChildForm] = useState(() => emptyChildForm(''));
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [entryForm, setEntryForm] = useState(() => emptyEntryForm());
  const [entryContext, setEntryContext] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const tree = useMemo(
    () => mergeCoaTreeWithLiveData(baseTree, liveData || {}),
    [baseTree, liveData],
  );

  const allChildCodes = useMemo(
    () => tree.flatMap((p) => p.children.map((c) => c.code)),
    [tree],
  );

  const refresh = useCallback(async () => {
    if (!gymId) {
      setBaseTree([]);
      setLoading(false);
      return;
    }
    try {
      setError('');
      setLoading(true);
      const next = await fetchCoaTree(gymId);
      setBaseTree(next);
    } catch (err) {
      setError(err.message || 'Failed to load chart of accounts');
      setBaseTree([]);
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openParentLedger = (parent) => {
    navigate(`/admin/finance/coa/${encodeURIComponent(parent.code)}`);
  };

  const openChildLedger = (parent, child, e) => {
    e?.stopPropagation?.();
    navigate(
      `/admin/finance/coa/${encodeURIComponent(parent.code)}/${encodeURIComponent(child.code)}`,
    );
  };

  const openAddChild = (parentCode, e) => {
    e?.stopPropagation?.();
    setChildForm(emptyChildForm(parentCode, allChildCodes));
    setShowChildModal(true);
  };

  const openAddEntryForChild = (parent, child, e) => {
    e?.stopPropagation?.();
    if (child.isSystem) return;
    setEntryContext({ parent, child });
    setEntryForm(emptyEntryForm(child.code));
    setShowEntryModal(true);
  };

  const handleSaveChild = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const created = await createCoaChildRemote(childForm, gymId);
      await refresh();
      setShowChildModal(false);
      toast.showSaved('Child account');
      navigate(
        `/admin/finance/coa/${encodeURIComponent(created.parentCode)}/${encodeURIComponent(created.code)}`,
      );
    } catch (err) {
      toast.showError(err.message || 'Failed to add child account');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!entryForm.childCode) {
      toast.showError('Select a child account.');
      return;
    }
    try {
      setSaving(true);
      await createCoaLedgerEntryRemote(entryForm.childCode, {
        date: entryForm.date,
        description: entryForm.description,
        ref: entryForm.ref,
        debit: Number(entryForm.debit) || 0,
        credit: Number(entryForm.credit) || 0,
        paymentMethod: isCoaCashBankExpenseParent(entryContext?.parent?.code)
          ? (entryForm.paymentMethod === 'bank' ? 'bank' : 'cash')
          : undefined,
      }, gymId);
      await refresh();
      setShowEntryModal(false);
      toast.showSaved('Ledger entry');
      if (entryContext?.parent) {
        navigate(
          `/admin/finance/coa/${encodeURIComponent(entryContext.parent.code)}/${encodeURIComponent(entryForm.childCode)}`,
        );
      }
    } catch (err) {
      toast.showError(err.message || 'Failed to add ledger entry');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteChild = (child, e) => {
    e?.stopPropagation?.();
    if (child?.isSystem) return;
    setDeleteTarget(child);
  };

  const handleDeleteChild = async () => {
    if (!deleteTarget?.code || deleteTarget.isSystem) return;
    try {
      setDeleting(true);
      await deleteCoaChildRemote(deleteTarget.code, gymId);
      toast.showDeleted(deleteTarget.name || 'Child ledger');
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete child ledger');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="owner-card rec-panel-card admin-finance-section admin-finance-coa-tree-section">
      <div className="admin-finance-member-header">
        <h2 className="owner-card-title">Chart of Accounts</h2>
        <p className="owner-placeholder-text" style={{ margin: 0 }}>
          Click a parent or child to open its ledger
        </p>
      </div>

      {error && (
        <p className="owner-placeholder-text" style={{ padding: '0 24px 12px 20px', color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}
      {loading && (
        <p className="owner-placeholder-text" style={{ padding: '0 24px 12px 20px' }}>
          Loading chart of accounts...
        </p>
      )}

      {!loading && tree.length === 0 && !error ? (
        <p className="owner-placeholder-text admin-finance-coa-tree-empty" style={{ margin: '0 24px 24px 20px' }}>
          No parent accounts found. Run the CoA SQL in Supabase, then refresh.
        </p>
      ) : (
        <div className="admin-finance-coa-parent-grid">
          {tree.map((parent) => {
            const childCount = (parent.children || []).length;
            return (
              <article
                key={parent.code}
                className="admin-finance-coa-parent-card"
              >
                <header className="admin-finance-coa-parent-card-head">
                  <button
                    type="button"
                    className="admin-finance-coa-parent-main"
                    onClick={() => openParentLedger(parent)}
                  >
                    <span className="admin-finance-coa-parent-card-titles">
                      <span className="admin-finance-coa-parent-card-title">{parent.name}</span>
                      <span className="admin-finance-coa-parent-card-meta">
                        {childCount} child{childCount === 1 ? '' : 'ren'}
                      </span>
                    </span>
                    <span className="admin-finance-coa-parent-trail">
                      <span className="admin-finance-coa-count-badge">{childCount}</span>
                      <MdChevronRight className="admin-finance-coa-chevron" />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="admin-finance-coa-icon-btn"
                    title="Add child"
                    aria-label={`Add child under ${parent.name}`}
                    onClick={(e) => openAddChild(parent.code, e)}
                  >
                    <MdAdd />
                  </button>
                </header>

                <ul className="admin-finance-coa-child-names">
                  {(parent.children || []).length ? (parent.children || []).map((child) => (
                    <li key={child.code}>
                      <div className="admin-finance-coa-child-row">
                        <button
                          type="button"
                          className="admin-finance-coa-child-name-btn"
                          onClick={(e) => openChildLedger(parent, child, e)}
                        >
                          {child.name}
                        </button>
                        {!child.isSystem && (
                          <>
                            <button
                              type="button"
                              className="admin-finance-coa-child-entry-btn"
                              title="Add entry"
                              aria-label={`Add entry for ${child.name}`}
                              onClick={(e) => openAddEntryForChild(parent, child, e)}
                            >
                              <MdNoteAdd />
                            </button>
                            <button
                              type="button"
                              className="admin-finance-coa-child-entry-btn admin-finance-coa-child-delete-btn"
                              title="Delete child ledger"
                              aria-label={`Delete ${child.name}`}
                              onClick={(e) => openDeleteChild(child, e)}
                            >
                              <MdDelete />
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  )) : (
                    <li className="admin-finance-coa-child-empty">No children</li>
                  )}
                </ul>
              </article>
            );
          })}
        </div>
      )}

      {showChildModal && (
        <div className="subscription-modal-overlay" onClick={() => setShowChildModal(false)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Add Child Account</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowChildModal(false)} aria-label="Close">×</button>
            </div>
            <form className="subscription-form" onSubmit={handleSaveChild}>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-child-parent">Parent</label>
                <input id="coa-child-parent" className="owner-form-input" value={childForm.parentCode} readOnly />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-child-name">Account Name</label>
                <input
                  id="coa-child-name"
                  className="owner-form-input"
                  value={childForm.name}
                  onChange={(e) => setChildForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-child-code">Code</label>
                <input
                  id="coa-child-code"
                  className="owner-form-input"
                  value={childForm.code}
                  onChange={(e) => setChildForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-child-desc">Description</label>
                <input
                  id="coa-child-desc"
                  className="owner-form-input"
                  value={childForm.description}
                  onChange={(e) => setChildForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Child Account'}
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setShowChildModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEntryModal && entryContext && (
        <div className="subscription-modal-overlay" onClick={() => setShowEntryModal(false)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">
                Add Entry — {entryContext.child?.name || 'Child'}
              </h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowEntryModal(false)} aria-label="Close">×</button>
            </div>
            <form className="subscription-form" onSubmit={handleSaveEntry}>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-entry-date">Date</label>
                <input
                  id="coa-entry-date"
                  type="date"
                  className="owner-form-input"
                  value={entryForm.date}
                  onChange={(e) => setEntryForm((f) => ({ ...f, date: e.target.value }))}
                  required
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-entry-desc">Description</label>
                <input
                  id="coa-entry-desc"
                  className="owner-form-input"
                  value={entryForm.description}
                  onChange={(e) => setEntryForm((f) => ({ ...f, description: e.target.value }))}
                  required
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-entry-ref">Ref</label>
                <input
                  id="coa-entry-ref"
                  className="owner-form-input"
                  value={entryForm.ref}
                  onChange={(e) => setEntryForm((f) => ({ ...f, ref: e.target.value }))}
                />
              </div>
              {isCoaCashBankExpenseParent(entryContext.parent?.code) ? (
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="coa-entry-method">Type</label>
                  <select
                    id="coa-entry-method"
                    className="owner-form-input"
                    value={entryForm.paymentMethod || 'cash'}
                    onChange={(e) => setEntryForm((f) => ({
                      ...f,
                      paymentMethod: e.target.value === 'bank' ? 'bank' : 'cash',
                    }))}
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                  </select>
                </div>
              ) : null}
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-entry-debit">Debit</label>
                <input
                  id="coa-entry-debit"
                  type="number"
                  min="0"
                  className="owner-form-input"
                  value={entryForm.debit}
                  onChange={(e) => setEntryForm((f) => ({ ...f, debit: e.target.value, credit: e.target.value ? '' : f.credit }))}
                />
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label" htmlFor="coa-entry-credit">Credit</label>
                <input
                  id="coa-entry-credit"
                  type="number"
                  min="0"
                  className="owner-form-input"
                  value={entryForm.credit}
                  onChange={(e) => setEntryForm((f) => ({ ...f, credit: e.target.value, debit: e.target.value ? '' : f.debit }))}
                />
              </div>
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Entry'}
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setShowEntryModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="subscription-modal-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Child Ledger</h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={() => !deleting && setDeleteTarget(null)}
                aria-label="Close"
                disabled={deleting}
              >
                ×
              </button>
            </div>
            <p className="owner-placeholder-text" style={{ marginBottom: 16 }}>
              Delete child ledger <strong>{deleteTarget.name}</strong>
              {deleteTarget.code ? <> ({deleteTarget.code})</> : null}? This removes the child
              account and all of its ledger entries.
            </p>
            <div className="subscription-form-actions">
              <button
                type="button"
                className="owner-btn rec-member-btn-delete"
                onClick={handleDeleteChild}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoaAccountTree;
